import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Typography, Box} from '@mui/material';

function MarkdownRenderer({content, variant = 'body2', sx = {}}) {
  const hasHeaders = /^#{1,6} /m.test(content || '');
  return (
    <Box
      sx={{
        textAlign: 'justify',
        paddingRight: hasHeaders ? '2.25em' : 0,
        '& p': {
          margin: '0.4em 0',
          paddingLeft: hasHeaders ? '1em' : 0,
        },
        '& p:first-of-type': {
          marginTop: 0,
        },
        '& p:last-child': {
          marginBottom: 0,
        },
        '& p:last-child > em:only-child': {
          display: 'inline-block',
          marginTop: '1em',
          marginLeft: hasHeaders ? '-1em' : 0,
        },
        '& ul, & ol': {
          margin: '0.2em 0',
          paddingLeft: hasHeaders ? '2.75em' : '1.75em',
          paddingRight: hasHeaders ? '2.25em' : 0,
        },
        '& li': {
          margin: 0,
          marginBottom: '0.1em',
          fontSize: '0.875rem',
          lineHeight: 1.43,
        },
        '& li:last-child': {
          marginBottom: 0,
        },
        '& li > p': {
          margin: 0,
        },
        '& strong': {
          fontWeight: 600,
        },
        '& em': {
          fontStyle: 'italic',
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '1em 0 0.25em 0',
          fontWeight: 600,
          lineHeight: 1.3,
        },
        '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type, & h4:first-of-type, & h5:first-of-type, & h6:first-of-type':
          {
            marginTop: 0,
          },
        '& h1': {fontSize: '1.5em'},
        '& h2': {fontSize: '1.35em'},
        '& h3': {fontSize: '1.2em'},
        '& h4': {fontSize: '1.1em'},
        '& code': {
          backgroundColor: 'rgba(0,0,0,0.05)',
          padding: '0.2em 0.4em',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& pre': {
          backgroundColor: 'rgba(0,0,0,0.05)',
          padding: '0.5em',
          borderRadius: '4px',
          overflow: 'auto',
          '& code': {
            backgroundColor: 'transparent',
            padding: 0,
          },
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          marginTop: 0,
          marginBottom: '0.5em',
          fontSize: '0.9em',
        },
        '& th, & td': {
          border: '1px solid #ddd',
          padding: '8px 12px',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: '#f5f5f5',
          fontWeight: 600,
        },
        '& tr:nth-of-type(even)': {
          backgroundColor: '#fafafa',
        },
        ...sx,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({node, children, ...props}) => {
            const extractText = (c) => {
              if (typeof c === 'string') return c;
              if (c?.props?.children) return extractText(c.props.children);
              if (Array.isArray(c)) return c.map(extractText).join('');
              return '';
            };
            const text = extractText(children);
            const isDisclaimer = text.startsWith('Disclaimer:');
            if (isDisclaimer) {
              return (
                <Typography component='p' variant={variant} style={{marginTop: '1em', paddingLeft: 0}} {...props}>
                  {children}
                </Typography>
              );
            }
            return (
              <Typography component='p' variant={variant} sx={{m: 0}} {...props}>
                {children}
              </Typography>
            );
          },
          strong: ({node, ...props}) => {
            return <strong {...props} />;
          },
          em: ({node, ...props}) => {
            return <em {...props} />;
          },
          ul: ({node, ...props}) => {
            return <ul {...props} />;
          },
          ol: ({node, ...props}) => {
            return <ol {...props} />;
          },
          li: ({node, ...props}) => {
            return <li {...props} />;
          },
          h1: ({node, ...props}) => {
            return (
              <Typography
                component='h1'
                variant='h5'
                sx={{m: 0, mb: 0.5, lineHeight: 1.3, fontWeight: 600}}
                {...props}
              />
            );
          },
          h2: ({node, ...props}) => {
            return (
              <Typography
                component='h2'
                variant='h6'
                sx={{m: 0, mb: 0.5, lineHeight: 1.3, fontWeight: 600}}
                {...props}
              />
            );
          },
          h3: ({node, ...props}) => {
            return (
              <Typography
                component='h3'
                variant='subtitle1'
                sx={{m: 0, mb: 0.5, lineHeight: 1.3, fontWeight: 600}}
                {...props}
              />
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

export default MarkdownRenderer;
